import _ from "lodash";
import client from "lib/apolloClient/client";
import { cache } from "lib/apolloClient";
import update from "immutability-helper";

import {
  createDeadlineV2Mutation,
  deleteDeadlineV2Mutation,
  updateDeadlineV2Mutation,
  updateProjectDeadlineMutation,
  updateProjectDeadlineSubmissionMutation,
  deleteAttachmentMutation,
  updateAttachmentMutation,
  updateSubmissionItemResponseMutation,
} from "./DeadlinesV2Mutations";
import {
  getProjectGroupDeadlinesV2Query,
  getProjectGroupDeadlinesCountQuery,
  getDeadlinesV2BasicDetailsQuery,
  getProjectGroupProjectsWithDeadlineDetailsQuery,
  getProjectDetailsWithTaskQuery,
} from "./Queries";
import { createAttachmentMutationForProjects } from "Projects/modules/ProjectMutations";
import { projectTaskSubmissionItemResponseFragment } from "./DeadlinesV2Fragments";

import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

export const createDeadlineV2 = ({
  input,
  refetchQueries = true,
  extraParams: { getProjectGroupDeadlinesV2Filters } = {},
}) => {
  return async dispatch => {
    try {
      await client.mutate({
        mutation: createDeadlineV2Mutation,
        variables: { input },
        awaitRefetchQueries: true,
        refetchQueries: refetchQueries
          ? [
              {
                query: getProjectGroupDeadlinesV2Query,
                variables: getProjectGroupDeadlinesV2Filters,
              },
              {
                query: getProjectGroupDeadlinesCountQuery,
                variables: { projectGroupId: input?.projectGroupId },
              },
            ]
          : [],
      });

      dispatch(
        setToastMsg({
          msg: "toastMsgs:deadline_creation_success",
          type: "success",
        })
      );
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteDeadlineV2 = ({
  input,
  refetchQueries = true,
  extraParams: { getProjectGroupDeadlinesV2Filters } = {},
  projectGroupId,
}) => {
  return async dispatch => {
    try {
      await client.mutate({
        mutation: deleteDeadlineV2Mutation,
        variables: { input },
        awaitRefetchQueries: true,
        refetchQueries: refetchQueries
          ? [
              {
                query: getProjectGroupDeadlinesV2Query,
                variables: getProjectGroupDeadlinesV2Filters,
              },
              {
                query: getProjectGroupDeadlinesCountQuery,
                variables: { projectGroupId },
              },
            ]
          : [],
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:deadline_delete_success",
          type: "success",
        })
      );
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateDeadlineV2 = ({
  input,
  sourceType,
  extraParams: { getProjectGroupDeadlinesV2Filters } = {},
}) => {
  return async dispatch => {
    let refetchQueries = [];
    switch (sourceType) {
      case "projectDeadline":
        refetchQueries = [
          {
            query: getDeadlinesV2BasicDetailsQuery,
            variables: {
              id: input?.id,
            },
          },
        ];
        break;
      case "calendar":
        refetchQueries = [];
        break;
      default:
        refetchQueries = [
          {
            query: getProjectGroupDeadlinesV2Query,
            variables: getProjectGroupDeadlinesV2Filters,
          },
        ];
    }

    try {
      await client.mutate({
        mutation: updateDeadlineV2Mutation,
        variables: { input },
        awaitRefetchQueries: true,
        refetchQueries: refetchQueries,
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:deadline_update_success",
          type: "success",
        })
      );
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateProjectDeadline = ({
  input,
  extraParams: {
    vars: {
      projectGroupProjectsWithDeadlineFilters,
      getProjectDetailsWithTaskFilters,
    } = {},
    query,
  } = {},
  sourceType,
  isBulkAction,
}) => {
  const id = _.first(input?.taskIds);

  return async dispatch => {
    const refetchQueries = [];

    const { isComplete, taskIds } = input;

    let successToastMessage = {};
    let cachedData = {};
    const itemsCount = _.size(taskIds);
    switch (sourceType) {
      case "projectSubmissionDetails":
        {
          refetchQueries.push({
            query: getProjectDetailsWithTaskQuery,
            variables: getProjectDetailsWithTaskFilters,
          });
        }
        break;
      case "deadlinesPane":
        {
          cache.updateQuery(
            {
              query,
              variables: getProjectDetailsWithTaskFilters,
            },
            data => {
              cachedData = data;
              const tasks = data?.node?.tasks;
              const task = _.find(tasks, { id });
              const updatedTask = {
                ...task,
                isComplete,
              };
              const updatedTasks = _.map(tasks, t =>
                t.id === id ? updatedTask : t
              );

              const updatedData = update(data, {
                node: {
                  tasks: {
                    $set: updatedTasks,
                  },
                },
              });

              return updatedData;
            }
          );

          successToastMessage = isComplete
            ? {
                msg: "toastMsgs:third_pane_deadline_complete_success",
                type: "success",
              }
            : {
                msg: "toastMsgs:third_pane_deadline_pending_success",
                type: "success",
              };
        }
        break;
      default: {
        refetchQueries.push({
          query: getProjectGroupProjectsWithDeadlineDetailsQuery,
          variables: projectGroupProjectsWithDeadlineFilters,
        });
        successToastMessage = isBulkAction
          ? {
              msg: "toastMsgs:bulk_deadline_complete_success",
              type: "success",
              locale_params: [
                {
                  key: "label",
                  value: isComplete ? "common:completed" : "common:pending",
                  isPlainText: false,
                },
                {
                  key: "count",
                  value: itemsCount,
                  isPlainText: true,
                },
              ],
            }
          : {
              msg: "toastMsgs:deadline_complete_success",
              type: "success",
              locale_params: [
                {
                  key: "label",
                  value: isComplete ? "common:completed" : "common:pending",
                  isPlainText: false,
                },
              ],
            };
      }
    }

    try {
      if (_.size(taskIds) != 0) {
        await client.mutate({
          mutation: updateProjectDeadlineMutation,
          variables: { input },
          awaitRefetchQueries: true,
          refetchQueries,
        });
      }
      if (!_.isEmpty(successToastMessage)) {
        dispatch(setToastMsg(successToastMessage));
      }
    } catch (e) {
      if (sourceType === "deadlinesPane") {
        cache.writeQuery({
          query,
          variables: getProjectDetailsWithTaskFilters,
          data: cachedData,
        });
      }
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateProjectDeadlineSubmission = ({
  input,
  extraParams: {
    vars: {
      projectGroupProjectsWithDeadlineFilters,
      getProjectDetailsWithTaskFilters,
    } = {},
  } = {},
  sourceType,
  isBulkAction,
  studentName,
}) => {
  return async dispatch => {
    const refetchQueries = [];
    let successToastMessage = {};

    const { submissionIds, isLocked } = input;

    switch (sourceType) {
      case "projectSubmissionDetails":
        {
          refetchQueries.push({
            query: getProjectDetailsWithTaskQuery,
            variables: getProjectDetailsWithTaskFilters,
          });
        }
        break;
      default: {
        refetchQueries.push({
          query: getProjectGroupProjectsWithDeadlineDetailsQuery,
          variables: projectGroupProjectsWithDeadlineFilters,
        });
        if (isBulkAction) {
          if (_.has(input, "isReturned")) {
            successToastMessage = {
              msg: "toastMsgs:bulk_resubmission_request_success",
              type: "success",
              locale_params: [
                {
                  key: "count",
                  value: _.size(submissionIds),
                  isPlainText: true,
                },
              ],
            };
          } else if (_.has(input, "isLocked")) {
            successToastMessage = {
              msg: "toastMsgs:bulk_submission_lock_status_success",
              type: "success",
              locale_params: [
                {
                  key: "label",
                  value: isLocked ? "common:closed" : "common:open",
                  isPlainText: false,
                },
                {
                  key: "count",
                  value: _.size(submissionIds),
                  isPlainText: true,
                },
              ],
            };
          }
        } else {
          if (_.has(input, "isReturned")) {
            successToastMessage = {
              msg: "toastMsgs:resubmission_request_success",
              type: "success",
            };
          } else if (_.has(input, "isLocked")) {
            successToastMessage = {
              msg: "toastMsgs:submission_lock_status",
              type: "success",
              locale_params: [
                {
                  key: "label",
                  value: isLocked ? "common:closed" : "common:open",
                  isPlainText: false,
                },
                {
                  key: "studentName",
                  value: studentName,
                  isPlainText: true,
                },
              ],
            };
          }
        }
      }
    }

    try {
      if (_.size(submissionIds) != 0) {
        await client.mutate({
          mutation: updateProjectDeadlineSubmissionMutation,
          variables: { input },
          awaitRefetchQueries: true,
          refetchQueries,
        });
      }
      if (!_.isEmpty(successToastMessage)) {
        dispatch(setToastMsg(successToastMessage));
      }
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteAttachment = ({
  input,
  extraParams: {
    vars: {
      getProjectDetailsWithTaskFilters,
      projectGroupProjectsWithDeadlineFilters,
    } = {},
  } = {},
  sourceType,
}) => {
  return async dispatch => {
    const refetchQueries = [];

    switch (sourceType) {
      case "allSubmissions":
        {
          refetchQueries.push({
            query: getProjectGroupProjectsWithDeadlineDetailsQuery,
            variables: projectGroupProjectsWithDeadlineFilters,
          });
        }
        break;
      default: {
        refetchQueries.push({
          query: getProjectDetailsWithTaskQuery,
          variables: getProjectDetailsWithTaskFilters,
        });
      }
    }

    try {
      await client.mutate({
        mutation: deleteAttachmentMutation,
        variables: { ...input },
        awaitRefetchQueries: true,
        refetchQueries,
      });
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const createAttachment = ({
  input,
  extraParams: { vars: { getProjectDetailsWithTaskFilters } = {} } = {},
}) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: createAttachmentMutationForProjects,
        variables: { input },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getProjectDetailsWithTaskQuery,
            variables: getProjectDetailsWithTaskFilters,
          },
        ],
      });
      return _.get(response, "data.platform.createAttachment");
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateAttachment = ({
  input,
  extraParams: {
    vars: {
      getProjectDetailsWithTaskFilters,
      projectGroupProjectsWithDeadlineFilters,
    } = {},
  } = {},
  sourceType,
}) => {
  return async dispatch => {
    const refetchQueries = [];
    switch (sourceType) {
      case "allSubmissions":
        {
          refetchQueries.push({
            query: getProjectGroupProjectsWithDeadlineDetailsQuery,
            variables: projectGroupProjectsWithDeadlineFilters,
          });
        }
        break;
      default: {
        refetchQueries.push({
          query: getProjectDetailsWithTaskQuery,
          variables: getProjectDetailsWithTaskFilters,
        });
      }
    }

    try {
      await client.mutate({
        mutation: updateAttachmentMutation,
        variables: { input },
        awaitRefetchQueries: true,
        refetchQueries,
      });
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateSubmissionItemResponse = ({ input }) => {
  cache.updateFragment(
    {
      id: `ProjectTaskSubmissionItemResponse:${input?.id}`,
      fragment: projectTaskSubmissionItemResponseFragment,
      fragmentName: "projectTaskSubmissionItemResponseItem",
    },
    data => {
      return {
        ...data,
        value: input?.value,
      };
    }
  );

  return async dispatch => {
    try {
      await client.mutate({
        mutation: updateSubmissionItemResponseMutation,
        variables: { input },
      });
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};
